import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useGetPaginatedCustomerSolarPlantListAdminQuery,
  useLazyViewSolarPlantStatusesQuery,
} from "../../redux/api/dashboard/dashboardAPI";
import { useLazyViewGenerationServiceProvidersQuery } from "../../redux/api/generation/generationAPI";
import {
  selectPlantStatus,
  setSelectPlantStatus,
  setSiteViewTabId,
  siteViewTabId,
} from "../../redux/features/sites/sites-slice";
import SiteCard from "../../shared/components/site-card/site-card";
import SiteEnergySource from "../../shared/components/site-energy-source/site-energy-source";
import SiteStatusCount from "../../shared/components/site-status-count/site-status-count";
import SitesTable from "../../shared/components/sites-table/sites-table";
import IListAllSolarPlantsRequestDTO from "../../shared/oversight-core/dtos/request-dtos/list-all-solar-plants-response-dto";
import IViewAllGenerationServiceProvidersResponseDTO from "../../shared/oversight-core/dtos/response-dtos/view-all-generation-service-providers-response-dto";
import ViewSolarPlantStatusesResponseDTO from "../../shared/oversight-core/dtos/response-dtos/view-solar-plant-statuses-response-dto";
import { EPlantStatus } from "../../shared/oversight-core/enums/plant-status";
import { ESolarPlantSize } from "../../shared/oversight-core/enums/solar-plant-size";
import { ESolarServiceProvider } from "../../shared/oversight-core/enums/solar-service-provider";
import { AppRoute } from "../../shared/oversight-core/interfaces/app-routes";
import { IGenerationServiceProviderView } from "../../shared/oversight-core/interfaces/generation-service-provider-view";
import { IInstance } from "../../shared/oversight-core/interfaces/instance";
import { IMapLocation } from "../../shared/oversight-core/interfaces/map-location";
import { IShallowSolarPlantView } from "../../shared/oversight-core/interfaces/shallow-solar-plant-view";
import GoogleMapDetails from "../../shared/oversight-core/shared-components/google-map-details/google-map-details";
import ActiveInactiveIndicator from "../../shared/oversight-core/ui-elements/active-inactive-indicator/active-inactive-indicator";
import { Option } from "../../shared/oversight-core/ui-elements/app-select/app-select";
import ButtonWithBadge from "../../shared/oversight-core/ui-elements/buttons/button-with-badge/button-with-badge";
import MaterialIcon from "../../shared/oversight-core/ui-elements/material-icon/material-icon";
import Pagination from "../../shared/oversight-core/ui-elements/pagination/pagination";
import Search from "../../shared/oversight-core/ui-elements/search/search";
import SpinnerModal from "../../shared/oversight-core/ui-elements/spinner/spinner";
import TabsIconButton from "../../shared/oversight-core/ui-elements/tabs-icon-button/tabs-icon-button";
import { getServiceProviderLogo } from "../../shared/oversight-core/utils/get-service-provider-logo";
import { getRelativeTimeFromTimestamp } from "../../shared/oversight-core/utils/time-utils";
import SitesFilteringSidePanel from "./sites-filtering-side-panel/sites-filtering-side-panel";
import styles from "./sites.module.scss";

const chartData: { value: number }[] = [
  { value: 100 },
  { value: 130 },
  { value: 210 },
  { value: 190 },
  { value: 240 },
  { value: 300 },
  { value: 220 },
  { value: 240 },
  { value: 190 },
  { value: 230 },
];

const initialFilters: IListAllSolarPlantsRequestDTO = {
  generationServiceProviderId: "",
  solarPlantSize: undefined,
  searchValue: "",
  plantStatus: undefined,
  pageNumber: 0,
};

const Sites = () => {
  const location = useLocation();

  const shouldUpdate = location.state?.updateCurrentState;

  const [filters, setFilters] = useState<IListAllSolarPlantsRequestDTO>({
    ...initialFilters,
  });
  const [searchText, setSearchText] = useState("");
  const [showFilteringSidePanel, setShowFilteringSidePanel] = useState(false);
  const [locations, setLocations] = useState<IMapLocation[]>([]);
  const [solarPlantList, setSolarPlantList] = useState<
    IShallowSolarPlantView[]
  >([]);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedSolarPlant, setSelectedSolarPlant] =
    useState<IShallowSolarPlantView>();
  const [filterBadgeValue, setFilterBadgeValue] = useState(0);
  const [solarBrandList, setSolarBrandList] = useState<Option[]>([
    { value: "", label: "", data: "" },
  ]);
  const [solarPlantStatuses, setSolarPlantStatuses] =
    useState<ViewSolarPlantStatusesResponseDTO>();
  const [sort, setSort] = useState(true);
  const [sortByName, setSortByName] = useState(true);
  const [sortByErrors, setSortByErrors] = useState(false);

  const [triggerGenerationServiceProviders] =
    useLazyViewGenerationServiceProvidersQuery();
  const [
    triggerSolarPlantStatuses,
    { isFetching: isFetchingSolarPlantStatuses },
  ] = useLazyViewSolarPlantStatusesQuery();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedTabId = useSelector(siteViewTabId);
  const selectedPlantStatus = useSelector(selectPlantStatus);

  const handleTabChange = (id: number) => {
    dispatch(setSiteViewTabId(id));
    setFilters({
      pageNumber: 0,
    });
  };

  const onSearch = () => {
    if (searchText) {
      setFilters({
        searchValue: searchText,
        plantStatus: selectedPlantStatus,
        pageNumber: 0,
      });
    }
  };

  const updateFilters = (
    generationServiceProviderId: string,
    solarPlantSize?: ESolarPlantSize,
    plantStatus?: EPlantStatus
  ) => {
    setFilters({
      generationServiceProviderId: generationServiceProviderId,
      solarPlantSize: solarPlantSize,
      plantStatus: plantStatus,
      pageNumber: 0,
    });
  };

  useEffect(() => {
    if (!searchText) {
      setFilters({ ...initialFilters, plantStatus: selectedPlantStatus });
    }
  }, [searchText, selectedPlantStatus]);

  useEffect(() => {
    if (selectedPlantStatus) {
      setFilters({
        plantStatus: selectedPlantStatus,
        pageNumber: 0,
      });
    }
  }, [selectedPlantStatus]);

  const customerSolarPlantListRequestParam = useMemo(() => {
    const filterParams: IListAllSolarPlantsRequestDTO = {
      pageNumber: filters.pageNumber,
      pageSize: selectedTabId === 1 ? 10 : 6,
      searchValue: filters.searchValue,
      sortBy: sortByErrors ? "errors" : "plant_name",
      ascending: sort,
    };

    if (filters.generationServiceProviderId) {
      filterParams.generationServiceProviderId =
        filters.generationServiceProviderId;
    }

    if (filters.solarPlantSize) {
      filterParams.solarPlantSize = filters.solarPlantSize;
    }

    if (filters.plantStatus) {
      filterParams.plantStatus = filters.plantStatus;
    }

    return filterParams;
  }, [filters, selectedTabId, sort, sortByErrors]);

  const {
    data: customerSolarPlantListData,
    isFetching: isFetchingCustomerSolarPlantList,
    refetch,
  } = useGetPaginatedCustomerSolarPlantListAdminQuery(
    customerSolarPlantListRequestParam
  );

  useEffect(() => {
    if (
      shouldUpdate ||
      filters.generationServiceProviderId ||
      filters.plantStatus ||
      filters.solarPlantSize
    ) {
      refetch();
    }
  }, [
    shouldUpdate,
    refetch,
    location.pathname,
    filters.generationServiceProviderId,
    filters.plantStatus,
    filters.solarPlantSize,
  ]);

  useEffect(() => {
    if (customerSolarPlantListData) {
      const filteringNullLocations =
        customerSolarPlantListData.genericPage.elements.filter(
          (e) => e.latitude !== null && e.longitude !== null
        );

      setSolarPlantList(customerSolarPlantListData.genericPage.elements);
      setTotalElements(customerSolarPlantListData.genericPage.totalElements);
      setLocations(
        filteringNullLocations.map((e) => {
          return {
            lat: e.latitude,
            lng: e.longitude,
            name: e.address,
          };
        })
      );
    }
  }, [customerSolarPlantListData]);

  useEffect(() => {
    triggerSolarPlantStatuses({ extended: false })
      .unwrap()
      .then((res: ViewSolarPlantStatusesResponseDTO) => {
        setSolarPlantStatuses(res);
      })
      .catch(() => {
        setSolarPlantStatuses(undefined);
      });
  }, [triggerSolarPlantStatuses]);

  useEffect(() => {
    triggerGenerationServiceProviders()
      .unwrap()
      .then((res: IViewAllGenerationServiceProvidersResponseDTO) => {
        const mappingSolarBrandList = res.generationServiceProviders.map(
          (g: IGenerationServiceProviderView) => ({
            value: g.identity,
            label: g.name,
            data: g.shortName,
          })
        );
        setSolarBrandList(mappingSolarBrandList);
      })
      .catch(() => {
        setSolarBrandList([]);
      });
  }, [triggerGenerationServiceProviders]);

  const getServiceProvider = (id: string): ESolarServiceProvider => {
    const findServiceProvider = solarBrandList.find(
      (item) => item.value === id
    );

    return findServiceProvider?.data as ESolarServiceProvider;
  };

  return (
    <div className="position-relative">
      <Row>
        <Col className="col-12 col-sm">
          <Row className="align-items-center gy-3">
            <Col className="col-12 col-sm-7 col-xl-4 pe-4 pe-sm-2">
              <Search
                onSearch={onSearch}
                handleSearchChange={(searchText) => {
                  setSearchText(searchText);
                }}
                placeholder="Search by name or address"
                value={searchText}
              />
            </Col>
            {/* <Col className="col-12 col-sm-auto pe-sm-0">
              <AppButton text="Add Site" className="px-4" />
            </Col> */}
            <Col className="col-4 col-sm-auto">
              <TabsIconButton
                buttons={[
                  { iconName: "format_list_bulleted", id: 1 },
                  { iconName: "distance", id: 2 },
                ]}
                onChange={handleTabChange}
                selectedTabId={selectedTabId}
              />
            </Col>
            <Col className="col-4 d-block d-sm-none px-0">
              <ButtonWithBadge
                text="Error Log"
                icon="emergency_home"
                iconColor="#69768B"
                onClick={() => navigate(AppRoute.ADMIN_SITES_ERROR_LOG)}
              />
            </Col>
            <Col className="col-4 d-block d-sm-none">
              <ButtonWithBadge
                badgeValue={filterBadgeValue}
                text="Filter"
                icon="filter_alt"
                onClick={() => setShowFilteringSidePanel(true)}
              />
            </Col>
          </Row>
        </Col>
        <Col className="col-auto d-none d-sm-block pe-0">
          <ButtonWithBadge
            text="Error Log"
            icon="emergency_home"
            iconColor="#69768B"
            onClick={() => navigate(AppRoute.ADMIN_SITES_ERROR_LOG)}
          />
        </Col>
        <Col className="col-auto d-none d-sm-block">
          <ButtonWithBadge
            badgeValue={filterBadgeValue}
            text="Filter"
            icon="filter_alt"
            onClick={() => setShowFilteringSidePanel(true)}
          />
        </Col>
      </Row>
      {selectedTabId === 1 ? (
        <>
          <div className={`${styles.siteStatusCardSContainer} p-2 mt-4`}>
            <Row className="row-cols-1 row-cols-sm-2 row-cols-xl-4 g-2">
              <Col>
                <SiteStatusCount
                  title="All Sites"
                  count={solarPlantStatuses?.numberOfTotalPlants || 0}
                  onClick={() => dispatch(setSelectPlantStatus(undefined))}
                  icon="solar_power"
                  iconColor="#469CD9"
                  isLoading={isFetchingSolarPlantStatuses}
                  selected={selectedPlantStatus === undefined}
                />
              </Col>
              <Col>
                <SiteStatusCount
                  title="Active Sites"
                  count={solarPlantStatuses?.numberOfOnlinePlants || 0}
                  onClick={() =>
                    dispatch(setSelectPlantStatus(EPlantStatus.GENERATING))
                  }
                  icon="fiber_manual_record"
                  iconColor="#29CC39"
                  isLoading={isFetchingSolarPlantStatuses}
                  selected={selectedPlantStatus === EPlantStatus.GENERATING}
                />
              </Col>
              <Col>
                <SiteStatusCount
                  title="Offline Sites"
                  count={solarPlantStatuses?.numberOfOfflinePlants || 0}
                  onClick={() =>
                    dispatch(setSelectPlantStatus(EPlantStatus.OFFLINE))
                  }
                  icon="wifi_off"
                  iconColor="#7D7B86"
                  isLoading={isFetchingSolarPlantStatuses}
                  selected={selectedPlantStatus === EPlantStatus.OFFLINE}
                />
              </Col>
              <Col>
                <SiteStatusCount
                  title="Errors"
                  count={solarPlantStatuses?.numberOfAlerts || 0}
                  onClick={() =>
                    dispatch(setSelectPlantStatus(EPlantStatus.FAULT))
                  }
                  icon="emergency_home"
                  iconColor="#E45E5E"
                  isLoading={isFetchingSolarPlantStatuses}
                  selected={selectedPlantStatus === EPlantStatus.FAULT}
                />
              </Col>
            </Row>
          </div>
          {solarPlantList && solarPlantList.length > 0 ? (
            <>
              <Row className="mt-4">
                <Col>
                  <SitesTable
                    headers={[
                      {
                        id: 1,
                        columnName: "Name",
                        isSort: sortByName,
                        sortFunction: () => {
                          setSort((ps) => !ps);
                          setSortByName(true);
                          setSortByErrors(false);
                        },
                        sort: sort,
                      },
                      { id: 2, columnName: "Load" },
                      { id: 3, columnName: "Solar" },
                      { id: 4, columnName: "Grid" },
                      { id: 5, columnName: "Battery" },
                      { id: 6, columnName: "Generator" },
                      { id: 7, columnName: "Temperature" },
                      { id: 8, columnName: "Last Update" },
                      {
                        id: 9,
                        columnName: "Errors",
                        isSort: sortByErrors,
                        sortFunction: () => {
                          setSort((ps) => !ps);
                          setSortByErrors(true);
                          setSortByName(false);
                        },
                        sort: sort,
                      },
                    ]}
                    data={solarPlantList.map((solarPlant) => {
                      return {
                        data: [
                          <>
                            <Row
                              className="ps-3 pe-2 align-content-center cursor-pointer h-100"
                              onClick={() =>
                                navigate(
                                  AppRoute.ADMIN_SITES +
                                    "/" +
                                    solarPlant.plantId,
                                  {
                                    state: {
                                      id: solarPlant.plantId,
                                      serviceProvider: getServiceProvider(
                                        solarPlant.generationServiceProviderId
                                      ),
                                    },
                                  }
                                )
                              }
                            >
                              <Col>
                                <Row>
                                  <Col className="text-dark font-weight-500 font-size-13">
                                    {solarPlant.plantName}
                                  </Col>
                                </Row>
                                <Row className="align-items-center row-cols-auto">
                                  <Col className="pe-0">
                                    <img
                                      src={getServiceProviderLogo(
                                        getServiceProvider(
                                          solarPlant.generationServiceProviderId
                                        )
                                      )}
                                      alt=""
                                    />
                                  </Col>
                                  <Col className="ps-2 pe-0 text-light font-weight-400 font-size-12">
                                    {solarPlant.address}
                                  </Col>
                                  <Col className="ps-2">
                                    <ActiveInactiveIndicator
                                      isActive={
                                        solarPlant.status !==
                                        EPlantStatus.OFFLINE
                                      }
                                    />
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </>,
                          <>
                            <Row className="align-content-end h-100">
                              <Col>
                                <SiteEnergySource
                                  data={chartData}
                                  value1={
                                    solarPlant.data.extended?.find(
                                      (item) => item.code === "consumption"
                                    )?.rawValue
                                  }
                                  stopColor="#E4515114"
                                  stroke="#D2595959"
                                  id="colorUv"
                                />
                              </Col>
                            </Row>
                          </>,
                          <>
                            <Row className="align-content-end h-100">
                              <Col>
                                <SiteEnergySource
                                  data={chartData}
                                  value1={
                                    getServiceProvider(
                                      solarPlant.generationServiceProviderId
                                    ) === ESolarServiceProvider.VRM
                                      ? solarPlant.data.extended?.find(
                                          (item) => item.code === "solar_yield"
                                        )?.rawValue
                                      : solarPlant.data.pac
                                  }
                                  stopColor="#29CC3914"
                                  stroke="#59D26B59"
                                  id="colorUx"
                                />
                              </Col>
                            </Row>
                          </>,
                          <>
                            <Row className="align-content-end h-100">
                              <Col>
                                <SiteEnergySource
                                  data={chartData}
                                  value1={
                                    solarPlant.data.extended?.find(
                                      (item) => item.code === "from_to_grid"
                                    )?.rawValue
                                  }
                                  stopColor="#CC882914"
                                  stroke="#CC882959"
                                  id="colorUz"
                                />
                              </Col>
                            </Row>
                          </>,
                          <>
                            <Row className="align-content-end h-100">
                              <Col>
                                <SiteEnergySource
                                  data={chartData}
                                  value1={
                                    getServiceProvider(
                                      solarPlant.generationServiceProviderId
                                    ) === ESolarServiceProvider.VRM
                                      ? solarPlant.data.extended?.find(
                                          (item) => item.code === "bs"
                                        )?.rawValue
                                      : solarPlant.data.battery_soc
                                  }
                                  value2={
                                    getServiceProvider(
                                      solarPlant.generationServiceProviderId
                                    ) === ESolarServiceProvider.VRM
                                      ? `${
                                          solarPlant.data.extended?.find(
                                            (item) => item.code === "bv"
                                          )?.rawValue
                                        } V`
                                      : `${solarPlant.data.battery_power} W`
                                  }
                                  batteryStatus={
                                    solarPlant.data.extended?.find(
                                      (item) => item.code === "bst"
                                    )?.formattedValue
                                  }
                                  stopColor="#4C29CC14"
                                  stroke="#4C29CC59"
                                  isBattery
                                  id="colorUc"
                                />
                              </Col>
                            </Row>
                          </>,
                          <>
                            <Row className="align-content-end h-100">
                              <Col>
                                <SiteEnergySource
                                  data={chartData}
                                  value1={
                                    solarPlant.data.extended?.find(
                                      (item) => item.code === "generator"
                                    )?.rawValue
                                  }
                                  stopColor="#A4579614"
                                  stroke="#A4579680"
                                  id="colorUq"
                                />
                              </Col>
                            </Row>
                          </>,
                          <>
                            <Row className="row-cols-auto align-items-center justify-content-center h-100">
                              {Object.entries(
                                solarPlant?.data?.tst?.instances ?? {}
                              ).map(([key, tstInstance], index) => {
                                const tsTInstance = (
                                  solarPlant?.data?.tsT?.instances as Record<
                                    string,
                                    IInstance
                                  >
                                )?.[key];

                                const length = Object.entries(
                                  solarPlant?.data?.tst?.instances ?? {}
                                ).length;

                                return tsTInstance ? (
                                  <>
                                    <Col
                                      key={index}
                                      className="text-light font-weight-400 font-size-10"
                                    >
                                      <div>{tstInstance.formattedValue}</div>
                                      <div>
                                        {tsTInstance?.formattedValue ?? "-"}
                                      </div>
                                    </Col>
                                    {index !== length - 1 && (
                                      <Col
                                        className={`${styles.divideLine} pe-0`}
                                      ></Col>
                                    )}
                                  </>
                                ) : (
                                  <></>
                                );
                              })}
                            </Row>
                          </>,
                          <>
                            <Row className="align-content-center h-100">
                              <Col className="text-light font-weight-400 font-size-12 text-center">
                                {solarPlant.data.last_timestamp ? (
                                  <>
                                    {getRelativeTimeFromTimestamp(
                                      solarPlant.data.last_timestamp
                                    )}
                                  </>
                                ) : (
                                  `-`
                                )}
                              </Col>
                            </Row>
                          </>,
                          <>
                            {solarPlant.hasErrors && (
                              <Row
                                className="align-content-center justify-content-center h-100 cursor-pointer"
                                onClick={() =>
                                  navigate(
                                    AppRoute.ADMIN_SITES +
                                      "/" +
                                      solarPlant.plantId,
                                    {
                                      state: {
                                        id: solarPlant.plantId,
                                        serviceProvider: getServiceProvider(
                                          solarPlant.generationServiceProviderId
                                        ),
                                        activeTab: 3,
                                      },
                                    }
                                  )
                                }
                              >
                                <Col className="col-auto">
                                  <MaterialIcon
                                    icon="emergency_home"
                                    color="#E45E5E"
                                    size={20}
                                    weight={600}
                                    className="user-select-none"
                                  />
                                </Col>
                              </Row>
                            )}
                          </>,
                        ],
                        cellWidths: [
                          200, 140, 140, 140, 140, 140, 120, 125, 90,
                        ],
                      };
                    })}
                  />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col>
                  <Pagination
                    itemsPerPage={10}
                    length={totalElements}
                    currentPage={filters.pageNumber + 1}
                    updateCurrentPage={(pn) => {
                      setFilters((ps) => ({
                        ...ps,
                        pageNumber: pn - 1,
                      }));
                    }}
                  />
                </Col>
              </Row>
            </>
          ) : (
            <Row className="container-dash mt-4 mx-0">
              <Col className="text-center text-light font-size-12">
                There are no&nbsp;
                {filters.plantStatus === EPlantStatus.DISABLED
                  ? `disabled sites`
                  : `sites`}
              </Col>
            </Row>
          )}
        </>
      ) : (
        <div className={`${styles.sitesMapView} p-3 mt-4`}>
          <Row className="align-items-start">
            <Col className="col-12 col-xl">
              <GoogleMapDetails
                locations={locations}
                selectedSite={{
                  latitude: selectedSolarPlant?.latitude ?? 0,
                  longitude: selectedSolarPlant?.longitude ?? 0,
                  address: selectedSolarPlant?.address ?? "",
                  value: selectedSolarPlant?.capacity ?? 0,
                }}
              />
            </Col>
            <Col className="mt-3 mt-xl-0">
              {filters.district && (
                <Row>
                  <Col className="text-dark font-weight-600 font-size-16 mt-2">
                    Site Near : {filters.district}
                  </Col>
                </Row>
              )}
              <Row className="mt-4">
                {solarPlantList.map((s, index) => (
                  <Col key={index} className="col-12 mt-2">
                    <SiteCard
                      solarPlant={s}
                      onClick={() =>
                        navigate(AppRoute.ADMIN_SITES + "/" + s.plantId, {
                          state: {
                            id: s.plantId,
                            serviceProvider: getServiceProvider(
                              s.generationServiceProviderId
                            ),
                          },
                        })
                      }
                      onSelect={() => setSelectedSolarPlant(s)}
                      selectedSolarPlant={selectedSolarPlant}
                      selectedTabId={selectedTabId}
                    />
                  </Col>
                ))}
              </Row>
              <Row className="mt-5">
                <Col>
                  <Pagination
                    itemsPerPage={6}
                    length={totalElements}
                    currentPage={filters.pageNumber + 1}
                    updateCurrentPage={(pn) => {
                      setFilters((ps) => ({
                        ...ps,
                        pageNumber: pn - 1,
                      }));
                    }}
                  />
                </Col>
              </Row>
              <Row>
                <Col className="text-light font-size-12 mt-3 text-center">
                  {solarPlantList.length}/{totalElements} Sites
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      )}
      <SitesFilteringSidePanel
        show={showFilteringSidePanel}
        onClose={() => setShowFilteringSidePanel(false)}
        updateFilters={updateFilters}
        setFilterBadgeValue={setFilterBadgeValue}
        filters={filters}
        solarBrandList={solarBrandList}
      />
      <SpinnerModal show={isFetchingCustomerSolarPlantList} />
    </div>
  );
};

export default Sites;
